import React from "react";

import type * as ozon from "@/api/ozon_fbs";
import { Button } from "@/components/ui/button";
import { errorDetail } from "@/lib/api-errors";

const SHIP_REFUSAL_CODES = new Set([
  "marks_checking",
  "marks_rejected",
  "marks_blocked",
  "marks_unknown",
]);

const SHIP_REFUSAL_TITLE: Record<ozon.OzonShipRefusal["code"], string> = {
  marks_checking: "Ozon проверяет коды",
  marks_rejected: "Ozon не принял коды",
  marks_blocked: "Коды передать нельзя",
  marks_unknown: "Обновите отправления",
};

/**
 * Отказ сборки по кодам маркировки → заголовок и строки для кладовщика.
 * `null` — ошибка другого рода (связь, 502): её показывает общий обработчик.
 *
 * ⚠️ У `marks_rejected` строка на КАЖДЫЙ код: «товар — начало кода — причина».
 * «Отправление не собралось» без кода кладовщику не помогает: в коробке может
 * быть десять одинаковых штук, и заменить надо конкретную.
 */
export function shipRefusal(err: unknown): { title: string; lines: string[] } | null {
  const d = errorDetail<ozon.OzonShipRefusal>(err);
  if (!d || !SHIP_REFUSAL_CODES.has(d.code)) return null;
  const lines =
    d.code === "marks_rejected" && d.problems?.length
      ? d.problems.map((p) => [p.product, p.code_head, p.reason].filter(Boolean).join(" — "))
      : d.detail.split("\n").filter(Boolean);
  return { title: SHIP_REFUSAL_TITLE[d.code], lines };
}

/**
 * Коды строки, которые Ozon не принял, и «Заменить код» у каждого.
 *
 * «Заменить» = снять этот скан (штатная ручка снятия, до сборки) и
 * отсканировать штуку заново: новый код ляжет в освободившийся слот, и
 * следующая «Собрать» отправит набор целиком ещё раз.
 */
export function MarkErrorList({
  errors,
  onReplace,
  busy,
}: {
  errors: ozon.OzonMarkError[];
  onReplace: (pickId: string) => void;
  busy: boolean;
}): React.ReactElement | null {
  if (!errors.length) return null;
  return (
    <ul className="mt-1 space-y-1">
      {errors.map((e) => (
        <li
          key={e.pick_id}
          className="flex items-center gap-2 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive"
        >
          <div className="min-w-0 flex-1">
            <div className="truncate font-mono">{e.code_head}</div>
            <div className="break-words">{e.error}</div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-8 shrink-0"
            disabled={busy}
            onClick={() => onReplace(e.pick_id)}
          >
            Заменить код
          </Button>
        </li>
      ))}
    </ul>
  );
}
